<template>
  <div class="bg-gray-50">
    <div class="max-w-7xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
      <div class="max-w-3xl mx-auto divide-y-2 divide-gray-200">
        <h2 class="text-center text-3xl font-extrabold text-gray-900 sm:text-4xl">FAQ</h2>
        <dl class="mt-6 divide-y divide-gray-200 prose text-gray-500 max-w-none text-base">
          <Disclosure as="div" v-for="faq in faqs" :key="faq.question" class="pt-6" v-slot="{ open }">
            <dt class="text-lg">
              <DisclosureButton
                class="text-left w-full flex justify-between items-start text-gray-400 focus:outline-none transition ease-in-out duration-300"
              >
                <span class="font-medium text-gray-900" :id="faq.id">
                  {{ faq.question }}
                </span>
                <span class="ml-6 h-7 flex items-center">
                  <ChevronDownIcon
                    :class="[open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']"
                    aria-hidden="true"
                  />
                </span>
              </DisclosureButton>
            </dt>
            <DisclosurePanel as="dd" class="mt-2 pr-12">
              <div class="text-base text-gray-500" v-html="faq.answer"></div>
            </DisclosurePanel>
          </Disclosure>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/outline";

const faqs = [
  {
    question: "Why pay for pdf conversion? I can already do that for free",
    answer: `<p>
  There are a many free online tools but they'll only get you part of the way. You'll have to manually
  fix up a lot of things, typically:
</p>
<ul>
  <li>
    <b>Dates</b> - these will be converted exactly as is: "20 06" isn't a real date, you'll manually have to change
    that to "2021-06-20" in order for excel to recognise this as a date and you'll have to repeat that
    for every transaction in the statement.
  </li>
  <li>
    <b>Number</b> - amounts and balances like "R 1,000.35" will also be text, you'll have to strip the currency
    symbol and formatting (like thousands and decimal separators) manually otherwise you won't be able
    to add up the amounts in excel. Note: you have to do this twice for every transaction (once for
    the amount and again for the balance).
  </li>
  <li>
    then there are things like multi-line transactions, and transactions which span 2 pages all of
    which require manually correction ...
  </li>
</ul>

<p>
  These corrections must be repeated across every page in the statement. The net effect is that you're
  looking at spending 5 to 10 minutes manually converting each pdf. If you're only converting 1 file
  with only a handful of pages of transactions that might be fine, but if you're converting 100's of
  files and 1000's of transactions then that's a huge drag. If you're an accounting practise or a
  lender then this is simply not feasible.
</p>

<p>
  Many companies hire "back-office" staff or "credit-clerks" to manually perform this function. This
  is tedious work which is subject to a lot of human error and ultimately costs a lot more per-pdf
  than Spike's per-pdf charge.
</p>`,
  },

  {
    id: "faq-free",
    question: "What do I get on the free trial?",
    answer: `<p>
  Upon registration you will be allocated 10 free credits. That allows you to convert 10 pdfs for
  free. There after you will have to buy credits or sign up for one of our monthly plans.
</p>`,
  },

  {
    question: "Isn't web-scraping a better way to get transactions than statement processing?",
    answer: `<p>
  It's true that web-scraping and statement processing are both solutions to the same problem: namely
  they both provide a way for you to acquire bank transactions. Spike's initial product was actually a
  web-scraping API. Ultimately we discontinued this product and switched to statement processing
  because most clients that we spoke to had concerns over the security of web scraping. With a
  web-scraping service you have to send the username and password of a bank account to the
  web-scraping service. Banks aggressively discourage their customers from doing this and do not
  provide any form of protection or insurance against loss to customers who allow their accounts to be
  accessed in this fashion. Aside from the risk there are some other factors to consider:
</p>
<p>
  <b>reliability:</b>
  web-scraping tries to build an API on top of a service (internet banking) that was not designed for
  this purpose. There are a lot of moving parts to a web-scraping solution: reverse-engineering https
  calls, html fragment parsing and hard-coding to element identifiers, complicated microservice
  architecture to allow for scalability, etc... Each of these can fail and are sensitive to
  performance issues with the bank's website, as well as any changes to the structure of the bank's
  site. That's not to mention any quirks which might prevent some accounts from working e.g. accounts
  which exhibit some unusual variation from the testing accounts used by the web-scraping company.
</p>
<p>
  <b>duration:</b>
  web-scraping can only go back as far as the transaction history allows whereas statements can be
  imported over any time period (provided you have the statements);
</p>`,
  },

  {
    question: "Which pdf formats does Spike support?",
    answer: `<p>
  We currently support over 40 different formats across 10 banks, covering both
  <b>debit</b> and <b>credit card</b> statements. The full list of supported formats can be found in our
  <a href="https://docs.spikedata.co.za/statements/">statement library</a>. This library is updated whenever
  we receive new statements which happens from time to time either as new banks emerge (like Tyme, Discovery
  and Bank Zero) or as existing banks re-brand or change their statement templates.
</p>`,
  },

  {
    question: "How do I make use of the Spike API?",
    answer: `<p>
  The Spike API is currently only available on our fixed-monthly plans. We have extensive code
  documentation comprising both <a href="https://docs.spikedata.co.za/api/">OpenAPI</a>
  and <a href="https://www.postman.com/spikedata/workspace/spike-statements-api/documentation/14339623-f25b59e6-573d-4b20-9c71-0084a04a02c1">Postman</a>
  reference docs, as well as additional <a href="https://docs.spikedata.co.za/developer-guide/pdf/">guides</a>, and
  <a href="https://github.com/spikedata/samples">samples</a> . In addition, for JavaScript developers, we maintain public packages on NPM:
  <a href="https://www.npmjs.com/package/@spike/pdf-cli">@spike/pdf-cli</a> and
  <a href="https://www.npmjs.com/package/@spike/api-statements">@spike/api-statements</a>.
</p>
<p>
  See the full
  <a href="https://docs.spikedata.co.za/#api-resources">api resources</a>
  documentation.
</p>
<p>
  Note: If you are a startup that wants to use Spike and cannot afford the minimum plan then
  <a href="mailto:support@spikedata.co.za" title="support@spikedata.co.za">contact us</a>
  to see whether we can help.
</p>`,
  },

  {
    question: "How do I choose the correct pricing plan?",
    answer: `<p>
  Our plans can be divided into either: <b>pay-as-you-go</b> or <b>fixed</b>.
</p>

<p>
  <b>Pay-as-you-go</b> has no monthly cost, you just buy credits as you need them. You are charged 1 credit for each pdf
  that you process. There is a flat Rand cost per credit and this is more expensive than on a fixed plan.
</p>

<p>
  <b>Fixed</b> plans have a fixed monthly cost and an allotment of credits (a "<b>base</b>"). You are charged 1 credit
  for each pdf that you process. Unused credits from your base allotment don't "roll-over" to the next month - they must
  be used in the month within which they are allocated or they're lost. You can purchase more credits (<b>overage credits</b>)
  if you run out of your base credits and these overage credits do not expire. The Rand cost of your overage credits is
  determined by your plan - the larger fixed plans have reduced per-credit prices.
</p>

<p>
  See the <a href="#pricing">pricing table</a> for monthly and per-credit pricing.
</p>

<p>
  So to get the best value for money you need to pick the plan that's closest to your intended usage
  levels. If you don't know your usage levels then you can always start on a pay-as-you-go package and
  buy credits as you need until you figure out your, or choose a low-value fixed plan and top-up with
  extra credits if you run out.
</p>
<p>See our <a href="https://docs.spikedata.co.za/product/credits/">commercial guide</a> for full information on purchase &amp; deduction of credits.</p>`,
  },

  {
    question: "Do you offer refunds on remaining credits?",
    answer: `<p>
  We don't offer refunds on any plans for any pay-as-you-go, extra or base credits. See our
  <a href="https://docs.spikedata.co.za/product/credits/#refund-policy">refund policy</a>.
</p>`,
  },

  {
    question: "What happens when Spike fails to process a pdf?",
    answer: `<p>
  Occasionally <a href="javascript:void(0)" class="text-primary hover:text-primary-600 font-bold">1</a>
  you may encounter an error where Spike is unable to process your pdf. There are many reasons why
  this could occur - e.g. a mistake on your behalf (like you uploaded an encrypted statement but
  forgot to supply us with the password) or one on our side (like we didn't recognise the statement
  format). In the case where it's your error we charge you 0.1 credits (i.e. one tenth of a credit) -
  in order to compensate us for the processing cost on our side and to discourage abuse of our system.
  In the case where it's our fault you are not charged.
</p>
<div class="flex justify-start my-7">
  <a
    href="https://docs.spikedata.co.za/product/support/"
    class="
      inline-flex
      no-underline
      items-center
      px-7
      py-2
      shadow-sm
      text-base
      font-medium
      rounded-md
      text-primary
      bg-white
      hover:bg-gray-50
      focus:outline-none
    "
  >
    Learn more
  </a>
</div>
<ol class="text-primary font-bold space-y-3">
  <li>
    <span class="text-base text-gray-500 font-normal">
      This occurs less than 2% of the time (based on historical requests up to 2021)
    </span>
  </li>
</ol>`,
  },

  {
    question: "Do you offer support?",
    answer: `<p>
  Yes we do. On our premium plans we provide a fixed allotment of priority support requests
  <a href="javascript:void(0)" class="text-primary hover:text-primary-600 font-bold">1</a>
  . A priority support request is initiated by emailing our help address (support@spikedata.co.za). We
  guarantee to respond within 24 hours. On our other plans you can email us and we will respond as
  soon as we can, however we do not offer a guaranteed turnaround.
</p>

<ol class="text-primary font-bold space-y-3">
  <li>
    <span class="text-base text-gray-500 font-normal">
      you can find the monthly priority support request allotment per plan listed in the <a href="#pricing">pricing table</a>.
    </span>
  </li>
</ol>`,
  },
];

export default {
  components: {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    ChevronDownIcon,
  },
  setup() {
    const open = ref(false);

    return {
      faqs,
      open,
    };
  },
};
</script>
